<template>
  <div v-if="authorsWithBios && authorsWithBios.length > 0">
    <div class="author-block-wrapper">
      <template v-for="(author, index) in authorsWithBios">
        <div
          v-if="author.biographyBlocks?.length > 0"
          :key="author.slug || index"
          class="author-block"
        >
          <div class="author-header">
            <DynamicImage
              v-if="author.image"
              class="author-image"
              :image="author.image"
              :renditions="avatarRenditions"
            />
            <div v-else class="author-icon">
              <Icon name="eenvandaag-general:user" />
            </div>
            <div>
              <h3 class="author-title">{{ author.title }}</h3>
              <span v-if="author.function" class="author-function">{{ author.function }}</span>
            </div>
          </div>

          <div v-if="author.biographyBlocks.length > 0" class="author-biography">
            <Alinea :blocks="author.biographyBlocks" />
          </div>

          <div v-if="author.slug && author.showDetail" class="author-detail">
            <Button
              type="text"
              :to="`/biografieen/${author.slug}`"
              label="Alle bijdragen"
              icon="eenvandaag-general:arrow-right"
              icon-position="right"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface IProps {
  authors?: Array<any>;
  biographies?: Array<any>;
}

const props = withDefaults(defineProps<IProps>(), {
  authors: undefined,
  biographies: undefined,
});

const authorsWithBios = computed(() => {
  const returnData: any[] = [];

  props.authors?.forEach((authorWrapper, index) => {
    const authorData = authorWrapper.authorsId || {};

    const biographyWrapper = props.biographies ? props.biographies[index] : {};
    const biographyData = biographyWrapper?.biography || undefined;

    if (biographyData && biographyData?.blocks && biographyData?.blocks?.length > 0) {
      returnData.push({
        title: authorData.title || 'Onbekende auteur',
        slug: authorData.slug || '',
        image: authorData.image || '',
        function: authorData.function || '',
        showDetail: authorData.showDetail || false,
        biographyBlocks: biographyData.blocks || [],
      });
    }
  });

  return returnData;
});

const avatarRenditions = [
  {
    breakpoint: 'sm',
    size: 480,
    aspectRatio: '1x1',
  },
];
</script>

<style scoped lang="postcss">
.author-block-wrapper {
  --author-block-padding: var(--unit-20) var(--unit-18);
  --author-block-gap: var(--unit-24);
  display: flex;
  width: 100%;
  padding: var(--author-block-padding);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--author-block-gap);
  border-radius: 0;
  border: 1px solid hsl(var(--color-primary-100));

  @media (--lg-viewport) {
    --author-block-padding: var(--unit-32);
    --author-block-gap: var(--unit-32);
  }
}

.author-block {
  --author-block-gap: var(--unit-16);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--author-block-gap);

  @media (--lg-viewport) {
    --author-block-gap: var(--unit-16);
  }
}

.author-header {
  display: flex;
  align-items: center;
  gap: var(--unit-12);
}

.author-icon {
  width: var(--unit-48);
  height: var(--unit-48);
  background-color: hsl(var(--color-primary-100));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: var(--unit-30);
  color: hsl(var(--color-primary-700));
}
.author-image {
  border-radius: 50%;
  object-fit: cover;
  overflow: hidden;
  height: var(--unit-48);
  width: var(--unit-48);
}

.author-title {
  @mixin heading-md;
  font-weight: var(--font-weight-semi-bold);
  color: hsl(var(--color-primary-900));
}

.author-function {
  @mixin label-md;
  color: hsl(var(--color-primary-600));
}

.author-biography {
  @mixin body-md;
  color: hsl(var(--color-primary-900));
}

.author-block-caption {
  @mixin label-sm;
  padding-top: var(--unit-12);
  color: hsl(var(--color-primary-500));
}
</style>
